// src/context/AuthContext.jsx
import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Base URL for Django API
  const API_URL = process.env.REACT_APP_API_URL || '';
  //const API_URL = "http://44.199.254.15:8000/api/token/";

  // Initialize axios
  useEffect(() => {
    // Set base URL
    axios.defaults.baseURL = API_URL;
    
    // Get token from localStorage
    const token = localStorage.getItem('access_token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    
    // Check if user is already logged in
    checkAuthStatus();
  }, []);

  // Periodically refresh the access token so it never expires out from under
  // an open page — covers every route via this one provider instead of each
  // page re-implementing its own interval.
  useEffect(() => {
    if (!user) return;

    const refreshInterval = setInterval(async () => {
      try {
        await refreshToken();
        console.log('Token refreshed successfully');
      } catch (error) {
        console.error('Failed to refresh token:', error);
        if (error.response && error.response.status === 401) {
          logout();
          window.location.href = '/login';
        }
      }
    }, 5 * 60 * 1000); // Every 5 minutes

    return () => clearInterval(refreshInterval);
  }, [user]);

  // Check authentication status
  const checkAuthStatus = async () => {
    const token = localStorage.getItem('access_token');
    
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      // First, verify the token is valid
      await axios.post('/api/token/verify/', { token });
      
      // If token is valid, get user info
      await fetchUserInfo();
    } catch (error) {
      console.log('Token invalid or expired, trying to refresh...');
      
      // Try to refresh the token
      try {
        await refreshToken();
        await fetchUserInfo();
      } catch (refreshError) {
        console.log('Refresh failed, logging out...');
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  // Refresh token function
  const refreshToken = async () => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      throw new Error('No refresh token');
    }

    const response = await axios.post('/api/token/refresh/', {
      refresh: refreshToken
    });
    
    const { access } = response.data;
    
    // Store new access token
    localStorage.setItem('access_token', access);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access}`;
    
    return access;
  };

  // Fetch user info from Django
  const fetchUserInfo = async () => {
    try {
      const response = await axios.get('/api/user/me/');
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch user info:', error);
      
      // If endpoint doesn't exist, create a mock user from token
      const token = localStorage.getItem('access_token');
      if (token) {
        // Decode JWT token to get user info (basic implementation)
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const mockUser = {
            username: payload.username || 'user',
            email: payload.email || `${payload.username}@example.com`,
            first_name: payload.first_name || '',
            last_name: payload.last_name || '',
            is_staff: payload.is_staff || false,
            is_superuser: payload.is_superuser || false
          };
          setUser(mockUser);
          return mockUser;
        } catch (e) {
          console.error('Failed to decode token:', e);
          throw error;
        }
      }
      throw error;
    }
  };

  // Login function
  const login = async (username, password) => {
    try {
      console.log(`Attempting login for: ${username}`);
      
      // Get JWT tokens from Django
      const response = await axios.post('/api/token/', {
        username,
        password
      });
      
      const { access, refresh } = response.data;
      console.log('Login successful, tokens received');
      
      // Store tokens and username in localStorage
      localStorage.setItem('access_token', access);
      localStorage.setItem('refresh_token', refresh);
      localStorage.setItem('username', username);
      
      // Set axios default headers
      axios.defaults.headers.common['Authorization'] = `Bearer ${access}`;
      
      // Fetch user info
      const userInfo = await fetchUserInfo();
      console.log('User info fetched:', userInfo);
      
      return { 
        success: true, 
        user: userInfo 
      };
      
    } catch (error) {
      console.error('Login error:', error);
      
      let errorMessage = 'Login failed';
      
      if (error.response) {
        // Server responded with error
        const { status, data } = error.response;
        
        if (status === 401) {
          errorMessage = 'Invalid username or password';
        } else if (status === 400) {
          errorMessage = data.detail || 'Invalid request';
        } else if (status === 404) {
          errorMessage = 'Authentication endpoint not found';
        } else {
          errorMessage = `Server error (${status})`;
        }
      } else if (error.request) {
        // No response received
        errorMessage = 'Cannot connect to server.';
      } else {
        // Request setup error
        errorMessage = error.message;
      }
      
      return { 
        success: false, 
        error: errorMessage 
      };
    }
  };

  // Logout function
  const logout = () => {
    console.log('Logging out...');
    
    // Clear tokens and username from localStorage
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('username');
    
    // Clear axios header
    delete axios.defaults.headers.common['Authorization'];
    
    // Clear user state
    setUser(null);
    
    // Optional: Call Django logout endpoint if you have one
    // axios.post('/api/auth/logout/').catch(() => {});
  };

  // Register function (optional)
  const register = async (userData) => {
    try {
      const response = await axios.post('/api/register/', userData);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Registration error:', error);
      
      let errorMessage = 'Registration failed';
      if (error.response?.data) {
        // Handle Django validation errors
        const errors = error.response.data;
        if (errors.username) errorMessage = `Username: ${errors.username[0]}`;
        else if (errors.email) errorMessage = `Email: ${errors.email[0]}`;
        else if (errors.password) errorMessage = `Password: ${errors.password[0]}`;
        else if (errors.error) errorMessage = errors.error;
        else errorMessage = JSON.stringify(errors);
      }
      
      return { success: false, error: errorMessage };
    }
  };

  // Check if user is authenticated
  const isAuthenticated = () => {
    return !!user;
  };

  // Get auth token
  const getToken = () => {
    return localStorage.getItem('access_token');
  };

  // Expose a way for any page to force-refresh the user object from the API
  const refreshUser = async () => {
    try {
      const updated = await fetchUserInfo();
      return updated;
    } catch {
      return null;
    }
  };

  const value = {
    user,
    login,
    logout,
    register,
    loading,
    isAuthenticated,
    getToken,
    refreshToken,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};